const cameraConstraints: MediaStreamConstraints[] = [
  {
    video: {
      facingMode: "user",
    },
    audio: false,
  },
  {
    video: {
      facingMode: "environment",
    },
    audio: false,
  },
  {
    video: true,
    audio: false,
  },
];

const cameraMicrophoneConstraints: MediaStreamConstraints[] = [
  {
    video: {
      facingMode: "user",
    },
    audio: true,
  },
  {
    video: {
      facingMode: "environment",
    },
    audio: true,
  },
  {
    video: true,
    audio: true,
  },
];

export const cameraPermissionMessage =
  "Camera permission denied. Please allow camera access to use the photo frame.";
export const cameraMicrophonePermissionMessage =
  "Camera or microphone permission denied. Please allow access to record videos and use the photo frame.";
export const cameraUnavailableMessage =
  "Camera not available. Try opening this page in Safari if Chrome has issues.";
export const cameraHttpsMessage =
  "This feature requires HTTPS to use the camera.";

function isPermissionError(error: unknown) {
  return (
    error instanceof DOMException &&
    (error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError" ||
      error.name === "SecurityError")
  );
}

function isNotFoundError(error: unknown) {
  return (
    error instanceof DOMException &&
    (error.name === "NotFoundError" || error.name === "DevicesNotFoundError")
  );
}

function isNotReadableError(error: unknown) {
  return (
    error instanceof DOMException &&
    (error.name === "NotReadableError" || error.name === "TrackStartError")
  );
}

function isFramedPage() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function getCameraSupportMessage() {
  if (typeof window === "undefined") return cameraUnavailableMessage;
  if (!window.isSecureContext) {
    return `${cameraHttpsMessage} Please open the secure wedding link and try Safari if Chrome has issues.`;
  }
  if (!navigator.mediaDevices?.getUserMedia) return cameraUnavailableMessage;
  return "";
}

export function getCameraErrorMessage(error: unknown) {
  const supportMessage = getCameraSupportMessage();
  if (supportMessage) return supportMessage;

  const iframeHint = isFramedPage()
    ? " If this page is embedded, the iframe must allow camera access."
    : "";

  if (isPermissionError(error)) {
    return `${cameraPermissionMessage} Try opening this page in Safari if Chrome has issues.${iframeHint}`;
  }

  if (isNotFoundError(error)) return `${cameraUnavailableMessage}${iframeHint}`;

  if (isNotReadableError(error)) {
    return `Camera not available. Another app may be using it. Try opening this page in Safari if Chrome has issues.${iframeHint}`;
  }

  if (error instanceof Error && error.message) {
    if (error.message.includes("HTTPS")) return error.message;
    if (error.message.includes("Camera not available")) return `${error.message}${iframeHint}`;
  }

  return `${cameraUnavailableMessage}${iframeHint}`;
}

export function getCameraMicrophoneErrorMessage(error: unknown) {
  const supportMessage = getCameraSupportMessage();
  if (supportMessage) return supportMessage;

  const iframeHint = isFramedPage()
    ? " If this page is embedded, the iframe must allow camera and microphone access."
    : "";

  if (isPermissionError(error)) {
    return `${cameraMicrophonePermissionMessage} Try opening this page in Safari if Chrome has issues.${iframeHint}`;
  }

  if (isNotFoundError(error)) {
    return `Camera or microphone not available. You can still choose photos/videos from your library.${iframeHint}`;
  }

  if (isNotReadableError(error)) {
    return `Camera or microphone not available. Another app may be using it. Try opening this page in Safari if Chrome has issues.${iframeHint}`;
  }

  return `Camera or microphone not available. You can still choose photos/videos from your library.${iframeHint}`;
}

export async function requestCameraStream() {
  const supportMessage = getCameraSupportMessage();
  if (supportMessage) throw new Error(supportMessage);

  let lastError: unknown = new Error(cameraUnavailableMessage);

  for (const constraints of cameraConstraints) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      if (isPermissionError(error)) break;
    }
  }

  throw lastError;
}

export async function attachCameraStreamToVideo(
  videoElement: HTMLVideoElement,
  stream: MediaStream
) {
  videoElement.srcObject = stream;
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.setAttribute("playsinline", "true");
  videoElement.setAttribute("webkit-playsinline", "true");
  videoElement.style.visibility = "visible";
  videoElement.style.opacity = "1";

  await videoElement.play();
}

export async function requestCameraMicrophoneStream() {
  const supportMessage = getCameraSupportMessage();
  if (supportMessage) throw new Error(supportMessage);

  let lastError: unknown = new Error(cameraUnavailableMessage);

  for (const constraints of cameraMicrophoneConstraints) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      if (isPermissionError(error)) break;
    }
  }

  throw lastError;
}
